/**
 * Pure opmaak van de capaciteitstijdlijn (spec werkkalender §8): waar komt elk blok op de balk.
 * Geen React, zodat het getest wordt zonder DOM (CLAUDE.md: geen renderingtests).
 */
import { addDays } from '@cloud/shared/calendar/clock'
import type { Availability, Interval } from '@cloud/shared/calendar/types'
import { type DayWindow, FULL_DAY, placeInterval } from './planning-layout'

export interface Block {
  kind: 'open' | 'break' | 'maintenance'
  leftPct: number
  widthPct: number
  start: string
  end: string
}

export function layoutRow(
  a: Availability,
  from: string,
  days: number,
  hours: DayWindow = FULL_DAY,
): Block[] {
  const place = (kind: Block['kind'], list: Interval[]) =>
    list.flatMap((iv): Block[] => {
      const at = placeInterval(iv.start, iv.end, from, days, hours)
      return at ? [{ kind, ...at, start: iv.start, end: iv.end }] : []
    })
  return [
    ...place('open', a.open),
    ...place('break', a.breaks),
    ...place('maintenance', a.maintenance),
  ]
}

export interface Closure {
  id: string
  date: string
  endDate: string | null
  kind: 'closed' | 'open'
  startTime: string | null
  endTime: string | null
  category: string
  label: Record<string, string>
}

export function closureDays(closures: Closure[], from: string, to: string): Map<string, Closure[]> {
  const out = new Map<string, Closure[]>()
  for (const c of closures) {
    for (let d = c.date; d <= (c.endDate ?? c.date); d = addDays(d, 1)) {
      if (d < from || d > to) continue
      out.set(d, [...(out.get(d) ?? []), c])
    }
  }
  return new Map([...out.entries()].sort(([a], [b]) => a.localeCompare(b)))
}

/**
 * De delen van `list` die op `date` vallen, afgeknipt op die dag: een nachtploeg 22:00-06:00
 * staat op de eerste dag als 22:00-24:00 en op de tweede als 00:00-06:00.
 */
export function intervalsOnDay(list: Interval[], date: string): Interval[] {
  const dayStart = `${date}T00:00`
  const dayEnd = `${addDays(date, 1)}T00:00`
  return list
    .filter((iv) => iv.start < dayEnd && iv.end > dayStart)
    .map((iv) => ({
      start: iv.start < dayStart ? dayStart : iv.start,
      end: iv.end > dayEnd ? `${date}T24:00` : iv.end,
    }))
}
